/**
 * What the aggregation control means, in one place.
 *
 * The header badge, the axis caption, the request the chart sends and the
 * statistics table all derive from `resolveAggregation`. If "auto" were decided
 * separately in each, the badge could say DAY while the data came back hourly -
 * so the client picks the interval once and sends it explicitly.
 */

import type { AggInterval, AggMethod, Aggregation } from "./store";

export const INTERVAL_OPTIONS: { key: AggInterval; label: string }[] = [
  { key: "none", label: "No Aggregation" },
  { key: "hour", label: "Hourly" },
  { key: "day", label: "Daily" },
  { key: "week", label: "Weekly" },
  { key: "month", label: "Monthly" },
  { key: "custom", label: "Custom" },
];

export const METHOD_OPTIONS: { key: AggMethod; label: string }[] = [
  { key: "max", label: "Max" },
  { key: "min", label: "Min" },
  { key: "sum", label: "Sum" },
  { key: "mean", label: "Mean" },
  { key: "count", label: "Count" },
  { key: "greatest", label: "Greatest" },
  { key: "peak-to-peak", label: "Peak-to-Peak" },
];

/** Must match CUSTOM_MIN_SECONDS / CUSTOM_MAX_SECONDS in the API's aggregate.py. */
export const CUSTOM_MIN_SECONDS = 60;
export const CUSTOM_MAX_SECONDS = 366 * 86400;

export interface ResolvedAggregation {
  auto: boolean;
  interval: AggInterval;
  customSeconds: number;
  method: AggMethod;
  rolling: boolean;
}

const DAY_MS = 86_400_000;

/**
 * The interval Auto chooses for a range.
 *
 * Raw up to two days, where every sample fits on a chart and aggregating would
 * hide the thing being looked at. Hourly up to a month. Daily beyond that -
 * which is how crossing data is read, "the greatest response each day" - until
 * the range is long enough that daily points stop being distinguishable.
 */
export function autoInterval(start: Date, end: Date): Exclude<AggInterval, "custom" | "month"> {
  const days = (end.getTime() - start.getTime()) / DAY_MS;
  if (days <= 2) return "none";
  if (days <= 31) return "hour";
  if (days <= 15 * 365) return "day";
  return "week";
}

export function resolveAggregation(
  agg: Aggregation,
  range: { start: Date; end: Date },
): ResolvedAggregation {
  if (agg.mode === "auto") {
    return {
      auto: true,
      interval: autoInterval(range.start, range.end),
      customSeconds: agg.customSeconds,
      method: "greatest",
      rolling: false,
    };
  }
  const customSeconds = Math.min(
    CUSTOM_MAX_SECONDS,
    Math.max(CUSTOM_MIN_SECONDS, Math.round(agg.customSeconds)),
  );
  return {
    auto: false,
    interval: agg.interval,
    customSeconds,
    method: agg.method,
    rolling: agg.interval !== "none" && agg.rolling,
  };
}

/** The `interval` query parameter: a name, or "<seconds>s" for custom. */
export function intervalParam(r: ResolvedAggregation): string {
  return r.interval === "custom" ? `${r.customSeconds}s` : r.interval;
}

export function formatSeconds(seconds: number): string {
  if (seconds % 86400 === 0) {
    const d = seconds / 86400;
    return `${d} day${d === 1 ? "" : "s"}`;
  }
  if (seconds % 3600 === 0) {
    const h = seconds / 3600;
    return `${h} hour${h === 1 ? "" : "s"}`;
  }
  if (seconds % 60 === 0) return `${seconds / 60} min`;
  return `${seconds} s`;
}

const INTERVAL_WORD: Record<"hour" | "day" | "week" | "month", string> = {
  hour: "HOUR",
  day: "DAY",
  week: "WEEK",
  month: "MONTH",
};

function intervalWord(r: ResolvedAggregation): string {
  if (r.interval === "custom") return formatSeconds(r.customSeconds).toUpperCase();
  return INTERVAL_WORD[r.interval as keyof typeof INTERVAL_WORD] ?? r.interval.toUpperCase();
}

export function methodLabel(method: AggMethod): string {
  return METHOD_OPTIONS.find((o) => o.key === method)?.label ?? method;
}

/** Header badge: "AUTO (DAY)", "NONE", "GREATEST · DAY", "MAX · ROLLING WEEK". */
export function aggregationBadge(r: ResolvedAggregation): string {
  if (r.interval === "none") return r.auto ? "AUTO (NONE)" : "NONE";
  const word = intervalWord(r);
  if (r.auto) return `AUTO (${word})`;
  const method = methodLabel(r.method).toUpperCase();
  return r.rolling ? `${method} · ROLLING ${word}` : `${method} · ${word}`;
}

/** The second axis line, "GREATEST per DAY". Null for raw samples. */
export function perCaption(r: ResolvedAggregation): string | null {
  if (r.interval === "none") return null;
  const method = methodLabel(r.method).toUpperCase();
  return r.rolling ? `${method} over rolling ${intervalWord(r)}` : `${method} per ${intervalWord(r)}`;
}

export interface SummaryStats {
  min: number;
  max: number;
  mean: number;
  median: number;
  /** Sample standard deviation (n - 1). */
  std: number;
  /** Signed value with the largest magnitude; a tie goes positive. */
  greatest: number;
  absMax: number;
  count: number;
}

export function summarize(values: number[]): SummaryStats | null {
  const v = values.filter((x) => Number.isFinite(x));
  const n = v.length;
  if (!n) return null;

  let min = Infinity;
  let max = -Infinity;
  let sum = 0;
  for (const x of v) {
    if (x < min) min = x;
    if (x > max) max = x;
    sum += x;
  }
  const mean = sum / n;

  let squares = 0;
  for (const x of v) squares += (x - mean) ** 2;
  const std = n > 1 ? Math.sqrt(squares / (n - 1)) : 0;

  const sorted = [...v].sort((a, b) => a - b);
  const mid = Math.floor(n / 2);
  const median = n % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;

  return {
    min,
    max,
    mean,
    median,
    std,
    greatest: Math.abs(max) >= Math.abs(min) ? max : min,
    absMax: Math.max(Math.abs(min), Math.abs(max)),
    count: n,
  };
}

export function summaryCsv(rows: { label: string; stats: SummaryStats }[], unit: string): string {
  const esc = (s: string) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);
  const lines = ["channel,unit,minimum,maximum,mean,median,std_dev,greatest,abs_max,count"];
  for (const { label, stats: s } of rows) {
    lines.push(
      [esc(label), esc(unit), s.min, s.max, s.mean, s.median, s.std, s.greatest, s.absMax, s.count].join(","),
    );
  }
  return lines.join("\n") + "\n";
}
